import type { SaveState } from '../Editor/useAutosave';
import Tooltip from './Tooltip';
import { longWhen, shortWhen } from '../../lib/publish';
import type { ScriptStatus } from '../../../shared/types';
import type { UnpublishedKind } from '../../../shared/publish';

/** 2A: what the client reads, for zone 2. Absent means the pre-publish bar. */
export interface PublishZone {
  status: ScriptStatus;
  /** Working copy against the published copy. */
  kind: UnpublishedKind;
  /** The round the client reads (2 for "v2"), null before the first publish. */
  round: number | null;
  publishedAt: number | null;
  /** Shown for a few seconds after a publish, then the clean form returns. */
  justPublished: { round: number; quiet: boolean; emailed: boolean } | null;
  /** For the tooltip sentence: "Yarra Bend Health is reading v2 ...". */
  clientName: string;
}

interface Props {
  state: SaveState;
  version: number;
  errorMsg: string | null;
  onRetry: () => void;
  clientPresent: boolean;
  publish?: PublishZone;
}

const ZONE = 'font-mono text-[10px] uppercase tracking-[0.12em]';
const DOT = 'w-1.5 h-1.5 rounded-full shrink-0';

/**
 * Zone 2 of the bar: what is happening to the document, as text only. Never a
 * button except when a save has failed, because a failed save is the one bit
 * of state that needs an action attached to it.
 *
 * It sheds words rather than items as the bar narrows: full sentence at lg,
 * an abbreviation at md, a bare dot on phones (which previously got no save
 * feedback at all).
 */
export default function DocState({ state, version, errorMsg, onRetry, clientPresent, publish }: Props) {
  const publishLine = publish ? <PublishLine publish={publish} state={state} /> : null;
  return (
    <div
      className={`inline-flex items-center gap-3 md:gap-3.5 min-w-0 overflow-hidden whitespace-nowrap ${ZONE}`}
    >
      {state === 'error' ? (
        <Tooltip content={`${errorMsg ?? 'Save failed'} · Retry`}>
          <button
            type="button"
            onClick={onRetry}
            aria-label={['Save failed', errorMsg, 'Retry'].filter(Boolean).join('. ')}
            className={`
              inline-flex items-center justify-center md:justify-start gap-1.5 shrink-0
              w-7 h-7 md:w-auto md:h-auto text-error
              focus:outline-none focus-visible:shadow-focus rounded-full
              hover:text-error/80 transition-colors duration-150
            `}
          >
            <span aria-hidden className={`${DOT} bg-error`} />
            <span aria-hidden className="hidden lg:inline">Save failed · Retry</span>
            <span aria-hidden className="hidden md:inline lg:hidden">Failed · Retry</span>
          </button>
        </Tooltip>
      ) : publishLine ? (
        publishLine
      ) : (
        <span className="inline-flex items-center gap-1.5 shrink-0 text-faint">
          <span aria-hidden className={`${DOT} ${dotClass(state)}`} />
          <span className="hidden lg:inline">{fullLabel(state, version)}</span>
          <span className="hidden md:inline lg:hidden">{shortLabel(state, version)}</span>
          {/* The dot alone carries the state on phones; say it out loud for a
              screen reader rather than leaving it silent. */}
          <span className="sr-only md:hidden">{fullLabel(state, version)}</span>
        </span>
      )}

      {state === 'error' && publish && publish.kind !== 'none' && (
        <span className="hidden lg:inline text-faint">Unpublished changes</span>
      )}

      {clientPresent && (
        <Tooltip content="A client is viewing this script right now.">
          <span className="inline-flex items-center gap-1.5 min-w-0 text-accent font-semibold">
            <span aria-hidden className={`${DOT} bg-accent animate-pulse`} />
            <span className="hidden lg:inline truncate">Client viewing</span>
            <span className="hidden md:inline lg:hidden truncate">Client</span>
            <span className="sr-only md:hidden">Client viewing</span>
          </span>
        </Tooltip>
      )}
    </div>
  );
}

function fullLabel(state: SaveState, version: number): string {
  switch (state) {
    case 'clean':
      return `v${version}`;
    case 'dirty':
      return 'Editing';
    case 'saving':
      return 'Saving';
    case 'saved':
      return `Saved · v${version}`;
    case 'stale':
      return 'Stale';
    case 'error':
      return 'Save failed';
  }
}

function shortLabel(state: SaveState, version: number): string {
  if (state === 'saved') return `v${version}`;
  return fullLabel(state, version);
}

function dotClass(state: SaveState): string {
  switch (state) {
    case 'dirty':
    case 'saving':
      return 'bg-accent animate-pulse';
    case 'saved':
    case 'stale':
      return 'bg-accent';
    case 'error':
      return 'bg-error';
    case 'clean':
      return 'bg-faint/40';
  }
}

/** The save word that follows "Unpublished changes ·". */
function saveWord(state: SaveState): string {
  switch (state) {
    case 'dirty':
      return 'Editing';
    case 'saving':
      return 'Saving';
    case 'stale':
      return 'Stale';
    default:
      return 'Saved';
  }
}

/**
 * Zone 2 once publishing exists (2A): which version the client reads, or that
 * the working copy has moved on. The gold dot is state only, never clickable;
 * the Publish button beside the status pill is the action.
 */
function PublishLine({ publish, state }: { publish: PublishZone; state: SaveState }) {
  const { kind, round, publishedAt, justPublished, clientName, status } = publish;
  const reader = clientName ? `${clientName} is` : 'The client is';

  if (status === 'draft' && round !== null) {
    const label = `The page is down. Last published v${round}${publishedAt ? ` on ${longWhen(publishedAt)}` : ''}.`;
    return (
      <span className="inline-flex items-center gap-1.5 shrink-0 text-faint">
        <span aria-hidden className={`${DOT} bg-faint/40`} />
        <span className="hidden lg:inline">{label}</span>
        <span className="hidden md:inline lg:hidden">Page down · v{round}</span>
        <span className="sr-only md:hidden">{label}</span>
      </span>
    );
  }

  if (justPublished) {
    const label = justPublished.quiet
      ? `v${justPublished.round} · Updated just now`
      : `v${justPublished.round} · Published just now${justPublished.emailed ? ' · Emailed the client' : ''}`;
    return (
      <span role="status" className="inline-flex items-center gap-1.5 shrink-0 text-accent">
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden className="shrink-0">
          <path d="M2.5 6.5l2.2 2.2L9.5 3.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="hidden lg:inline">{label}</span>
        <span className="hidden md:inline lg:hidden">{`v${justPublished.round} · Just now`}</span>
        <span className="sr-only md:hidden">{label}</span>
      </span>
    );
  }

  if (round === null) {
    // Never published: nothing outside the studio has seen it yet.
    const label = `Not shared yet · ${saveWord(state)}`;
    return (
      <span className="inline-flex items-center gap-1.5 shrink-0 text-faint">
        <span aria-hidden className={`${DOT} ${dotClass(state)}`} />
        <span className="hidden lg:inline">{label}</span>
        <span className="hidden md:inline lg:hidden">Not shared</span>
        <span className="sr-only md:hidden">{label}</span>
      </span>
    );
  }

  if (kind === 'none') {
    const when = publishedAt ? shortWhen(publishedAt) : '';
    const tip = publishedAt
      ? `${reader} reading v${round}, published ${longWhen(publishedAt)}.`
      : `${reader} reading v${round}.`;
    const tablet = publishedAt ? `v${round} · ${shortWhen(publishedAt).split(' ')[0]}` : `v${round}`;
    return (
      <Tooltip content={tip}>
        <span tabIndex={0} className="inline-flex items-center gap-1.5 shrink-0 text-faint focus:outline-none focus-visible:shadow-focus rounded-full">
          <span aria-hidden className={`${DOT} ${state === 'saving' || state === 'dirty' ? dotClass(state) : 'bg-faint/40'}`} />
          <span className="hidden lg:inline">{when ? `v${round} · Published ${when}` : `v${round}`}</span>
          <span className="hidden md:inline lg:hidden">{tablet}</span>
          <span className="sr-only md:hidden">{tip}</span>
        </span>
      </Tooltip>
    );
  }

  const headline = kind === 'internal' ? 'Unpublished internal changes' : 'Unpublished changes';
  const copyLabel = status === 'approved' ? `Approved copy is v${round}` : `Client reads v${round}`;
  const tip = status === 'approved'
    ? `${copyLabel}. Publish to show the client your changes.`
    : `${reader} reading v${round}. Publish to show them your changes.`;
  return (
    <Tooltip content={tip}>
      <span tabIndex={0} className="inline-flex items-center gap-1.5 min-w-0 shrink-0 text-faint focus:outline-none focus-visible:shadow-focus rounded-full">
        <span aria-hidden className={`${DOT} bg-gold`} />
        <span className="hidden lg:inline text-muted">{headline}</span>
        <span className="hidden lg:inline">· {saveWord(state)}</span>
        <span className="hidden md:inline lg:hidden text-muted">Unpublished</span>
        <span className="hidden xl:inline ml-2">{copyLabel}</span>
        <span className="sr-only md:hidden">{`${headline}. ${tip}`}</span>
      </span>
    </Tooltip>
  );
}
